import React from "react";
import { useNavigate } from "react-router-dom";
import { Row } from "react-bootstrap";
import useRegisterForm from "../../lib/Register/useRegisterForm";
import {
  TITLE_TH_LIST,
  onlyThai,
  onlyEng,
  onlyEngNoSpace,
  onlyEngTitle,
  onlyNumber,
  onlyEmail,
} from "../../lib/Register/registerValidators";
import RegisterShell from "./RegisterShell";
import { RegisterField, RegisterSelect } from "./RegisterFields";

// แต่ละสถานะระบุด้วยว่าต้องขอ CA หรือไม่
const SIGNATORY_STATUS_LIST = [
  { label: "ผู้มีอำนาจลงนาม", requiresCa: true },
  { label: "หัวหน้าโครงการ", requiresCa: false },
  { label: "พยานผู้รับทุน", requiresCa: false },
  { label: "ผู้ได้รับมอบอำนาจให้ลงนาม (มีหนังสือมอบอำนาจ)", requiresCa: true },
  { label: "พยานผู้ให้ทุนร่วม", requiresCa: false },
  { label: "ผู้ประสานงาน", requiresCa: false },
];

const SUCCESS_ALERT = {
  icon: "success",
  title: "ลงทะเบียนสำเร็จ",
  html: `
    <p>
      หน่วยบริหารจัดการทุนด้านการเพิ่มความสามารถการแข่งขัน ได้รับข้อมูลเรียบร้อยแล้ว
      ท่านจะได้รับอีเมลแจ้งรายละเอียดสำหรับการเข้าสู่ระบบภายใน 3-5 วันทำการ
    </p>
    <div class="app-swal-note">
      📌 หากท่านมีข้อสงสัยหรือต้องการข้อมูลเพิ่มเติม กรุณาติดต่อได้ที่<br/>
      โทรศัพท์: 02-109-5432 ต่อ 895 , 879 (ทีมสัญญา)<br/>
      หรือ อีเมล: pmuc.contract@nxpo.or.th
    </div>
  `,
  confirmButtonText: "ตกลง",
  allowOutsideClick: false, // ต้องกดตกลงก่อน ไม่งั้นคลิกนอกกล่องแล้วอ่านไม่ทัน
};

const statusRequiresCa = (label) =>
  SIGNATORY_STATUS_LIST.find((s) => s.label === label)?.requiresCa ?? false;

const rowStyle = { padding: "5px 10px" };

// หัวข้อกลุ่มในฟอร์ม
const SectionTitle = ({ children }) => (
  <div
    style={{
      margin: "26px 10px 14px",
      paddingBottom: "8px",
      fontSize: "17px",
      fontWeight: 700,
      color: "#053075",
      borderBottom: "1px solid rgba(5, 48, 117, 0.35)",
    }}
  >
    {children}
  </div>
);

// หน้าลงทะเบียนของ PMUC (เลือกมาจาก RegisterRoute ตาม service employee-pre-register-pmuc)
// caRequest ผูกกับสถานะที่เลือกโดยตรง (derive) ไม่มี checkbox ให้ติ๊กเอง
function RegisterPmuc({ prefix }) {
  const navigate = useNavigate();
  const form = useRegisterForm({
    prefix,
    requireStatus: true,
    requireSubOrganization: true, // ทุกสถานะต้องระบุหน่วยงาน
    resolveCaRequest: (values) => statusRequiresCa(values.signatoryStatus),
    successAlert: SUCCESS_ALERT,
  });
  const { bind, caRequest, values, setValue } = form;

  const handleStatusChange = (value) => {
    setValue("signatoryStatus", value);
    if (!statusRequiresCa(value)) form.resetCaFields();
  };

  return (
    <RegisterShell
      onCancel={() => navigate(`/${prefix}`)}
      onSubmit={() => form.handleSubmit({ role_pmuc: values.signatoryStatus })}
      submitDisabled={form.loading || !form.isFormValid}
      loading={form.loading}
      snackbarOpen={form.snackbarOpen}
      onCloseSnackbar={form.closeSnackbar}
    >
      {/* ---------- สถานะ ---------- */}
      <Row style={rowStyle}>
        <RegisterSelect
          {...bind("signatoryStatus")}
          onChange={handleStatusChange}
          lg={8}
          label="สถานะของท่านในการทำสัญญากับ หน่วยบริหารจัดการทุนด้านการเพิ่มความสามารถการแข่งขัน"
          placeholder="เลือกสถานะ"
          options={SIGNATORY_STATUS_LIST.map((s) => s.label)}
        />
      </Row>

      {/* ---------- ข้อมูลผู้ลงทะเบียน ---------- */}
      <SectionTitle>ข้อมูลผู้ลงทะเบียน</SectionTitle>

      <Row style={rowStyle}>
        <RegisterSelect
          {...bind("titleTh")}
          lg={4}
          label="คำนำหน้า"
          placeholder="คำนำหน้า"
          options={TITLE_TH_LIST}
        />
        <RegisterField
          {...bind("specialTitleTh", onlyThai)}
          onValidate={undefined}
          lg={4}
          label="คำนำหน้าชื่อพิเศษ (ภาษาไทย) (ถ้ามี)"
          placeholder="คำนำหน้าชื่อพิเศษ (ภาษาไทย)"
          maxLength={50}
        />
        <RegisterField
          {...bind("specialTitleEng", onlyEngTitle)}
          onValidate={undefined}
          lg={4}
          label="คำนำหน้าชื่อพิเศษ (ภาษาอังกฤษ) (ถ้ามี)"
          placeholder="คำนำหน้าชื่อพิเศษ (ภาษาอังกฤษ)"
          maxLength={50}
        />
      </Row>

      <Row style={rowStyle}>
        <RegisterField {...bind("nameTh", onlyThai)} label="ชื่อ (ภาษาไทย)" maxLength={50} />
        <RegisterField
          {...bind("surnameTh", onlyThai)}
          label="นามสกุล (ภาษาไทย)"
          maxLength={50}
        />
      </Row>

      <Row style={rowStyle}>
        <RegisterField {...bind("nameEng", onlyEng)} label="ชื่อ (ภาษาอังกฤษ)" maxLength={50} />
        <RegisterField
          {...bind("surnameEng", onlyEngNoSpace)}
          label="นามสกุล (ภาษาอังกฤษ)"
          maxLength={50}
        />
      </Row>

      <Row style={rowStyle}>
        <RegisterField
          {...bind("cid", onlyNumber)}
          lg={12}
          label={`เลขประจำตัวประชาชน (Identification Card)${
            caRequest ? "" : " (ถ้ามี)"
          } *ไม่ต้องมีขีดกลาง (-) หรือเว้นวรรค`}
          placeholder={caRequest ? "เลขประจำตัวประชาชน" : "เลขประจำตัวประชาชน (ถ้ามี)"}
          maxLength={20}
        />
      </Row>

      {/* ---------- ข้อมูลติดต่อ ---------- */}
      <SectionTitle>ข้อมูลติดต่อ</SectionTitle>

      <Row style={rowStyle}>
        <RegisterField
          {...bind("tel", onlyNumber)}
          lg={4}
          label="โทรศัพท์ (ถ้ามี) *ไม่ต้องมีขีดกลาง (-) หรือเว้นวรรค"
          placeholder="โทรศัพท์ (ถ้ามี)"
          maxLength={10}
        />
        <RegisterField
          {...bind("mobile", onlyNumber)}
          lg={4}
          label="โทรศัพท์มือถือ *ไม่ต้องมีขีดกลาง (-) หรือเว้นวรรค"
          placeholder="โทรศัพท์มือถือ"
          maxLength={10}
        />
        <RegisterField {...bind("email", onlyEmail)} lg={4} label="อีเมล" />
      </Row>

      {/* ---------- ข้อมูลหน่วยงาน ---------- */}
      <SectionTitle>ข้อมูลหน่วยงาน</SectionTitle>

      {/* ขึ้นเฉพาะสถานะที่ต้องขอ CA */}
      {caRequest && (
        <Row style={rowStyle}>
          <RegisterField
            {...bind("subOrganization")}
            label="ชื่อของหน่วยงาน"
            tooltip="กรุณานำข้อมูลจาก DBD เพื่อไม่ให้ชื่อบริษัท หรือองค์กรผิดพลาด"
            maxLength={200}
          />
          <RegisterField {...bind("address")} label="ที่อยู่" maxLength={300} />
        </Row>
      )}

      <Row style={{ ...rowStyle, paddingBottom: "15px" }}>
        <RegisterField
          {...bind("position")}
          onValidate={undefined}
          label="ตำแหน่ง (T) (ถ้ามี) (ภาษาไทย หรือ ภาษาอังกฤษ)"
          placeholder="ตำแหน่ง (ถ้ามี)"
        />
        <RegisterField
          {...bind("bizEmail", onlyEmail)}
          label={`อีเมลองค์กร${caRequest ? "" : " (ถ้ามี)"}`}
          placeholder="อีเมลองค์กร"
        />
      </Row>
    </RegisterShell>
  );
}

export default RegisterPmuc;